import React from 'react';
import styled from 'styled-components';
import { CalendarCheck, CalendarPlus, Clock, Sparkles, Video, Wifi } from 'lucide-react';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';
import { formatWhen, googleCalendarUrl } from './calendar';

/**
 * A consultoria da Whizr, no alto da página do Instagram: apresenta a consulta
 * grátis com um especialista e leva ao agendamento. Quando o produtor já tem
 * uma marcada, passa a mostrar a consulta (quando, com quem e o link).
 */
export default function ConsultingBanner({ consulting, onSchedule, onCancel }) {
    const { booking, eligibility } = consulting;

    if (booking) {
        return <BookedBanner booking={booking} onCancel={onCancel} />;
    }

    const blocked = eligibility && !eligibility.allowed;

    return (
        <Banner aria-label='Consultoria Whizr'>
            <Text>
                <Tag><Sparkles size={12} aria-hidden='true' /> Novo · Consultoria Whizr</Tag>
                <Title>Converse com um especialista da Whizr, de graça</Title>
                <Description>
                    Agora a Whizr tem consultoria: especialistas em marketing e eventos avaliam o seu perfil, as suas
                    vendas e dizem o que mudar para vender mais ingressos. A primeira conversa é por nossa conta.
                </Description>
                <Facts>
                    <Fact><Clock size={14} aria-hidden='true' /> 30 minutos</Fact>
                    <Fact><Wifi size={14} aria-hidden='true' /> Online</Fact>
                    <Fact><CalendarCheck size={14} aria-hidden='true' /> Você escolhe o horário</Fact>
                </Facts>
            </Text>

            <Action>
                <CtaButton type='button' disabled={blocked} onClick={onSchedule}>
                    <CalendarPlus size={16} aria-hidden='true' /> Agendar consulta grátis
                </CtaButton>
                {blocked && <ActionNote>{eligibility.message}</ActionNote>}
            </Action>
        </Banner>
    );
}

function BookedBanner({ booking, onCancel }) {
    return (
        <Banner aria-label='Sua consulta agendada'>
            <Text>
                <Tag><CalendarCheck size={12} aria-hidden='true' /> Consulta agendada</Tag>
                <Title>{formatWhen(booking.starts_at)}</Title>
                <Description>
                    Com {booking.consultant.name}
                    {booking.consultant.title && <>, {booking.consultant.title}</>}.
                    {booking.topic_label && <> Assunto: {booking.topic_label}.</>}
                    {' '}Mandamos a confirmação e o convite de calendário para o seu e-mail.
                </Description>
            </Text>

            <Action>
                {booking.meeting_url && (
                    <CtaLink href={booking.meeting_url} target='_blank' rel='noopener noreferrer'>
                        <Video size={16} aria-hidden='true' /> Link da reunião
                    </CtaLink>
                )}
                <SecondaryRow>
                    <SecondaryLink href={googleCalendarUrl(booking)} target='_blank' rel='noopener noreferrer'>
                        Adicionar ao Google Agenda
                    </SecondaryLink>
                    {booking.can_cancel && (
                        <SecondaryButton type='button' onClick={onCancel}>Cancelar consulta</SecondaryButton>
                    )}
                </SecondaryRow>
            </Action>
        </Banner>
    );
}

/** Convite curto, para dentro das abas (ex.: depois do plano de ação). Some quando já há consulta marcada. */
export function ConsultingCallout({ consulting, onSchedule }) {
    if (consulting.booking || (consulting.eligibility && !consulting.eligibility.allowed)) {
        return null;
    }

    return (
        <Callout>
            <div>
                <CalloutTitle>Quer ajuda para colocar isso em prática?</CalloutTitle>
                <CalloutText>Converse 30 minutos com um especialista da Whizr, de graça, e tire as suas dúvidas sobre o plano.</CalloutText>
            </div>
            <CalloutButton type='button' onClick={onSchedule}>
                <CalendarPlus size={15} aria-hidden='true' /> Agendar consulta grátis
            </CalloutButton>
        </Callout>
    );
}

const Banner = styled.section`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-bottom: 20px;
    padding: 22px;
    overflow: hidden;
    border-radius: 16px;
    color: ${colors.neutralLight};
    background: linear-gradient(120deg, ${panelAccent(colors.secondaryPurple)} 0%, color-mix(in srgb, ${panelAccent(colors.secondaryPurple)} 62%, #1a0b4d) 100%);

    &::after {
        content: '';
        position: absolute;
        top: -70px;
        right: -50px;
        width: 220px;
        height: 220px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.08);
        pointer-events: none;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
        padding: 26px 30px;
    }
`;

const Text = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 640px;
`;

const Tag = styled.span`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const Title = styled.h3`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
`;

const Description = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.88);
`;

const Facts = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    margin: 4px 0 0 0;
    padding: 0;
    list-style: none;
`;

const Fact = styled.li`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
`;

const Action = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.md}) {
        align-items: flex-end;
        max-width: 280px;
    }
`;

const ctaStyles = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 13px 20px;
    border-radius: 12px;
    border: none;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    transition: transform 0.15s ease, opacity 0.15s ease;
`;

const CtaButton = styled.button`
    ${ctaStyles}
    background: ${colors.neutralLight};
    color: ${panelAccent(colors.secondaryPurple)};
    cursor: pointer;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const CtaLink = styled.a`
    ${ctaStyles}
    background: ${colors.neutralLight};
    color: ${panelAccent(colors.secondaryPurple)};

    &:hover {
        transform: translateY(-1px);
    }
`;

const ActionNote = styled.span`
    font-size: 12px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.85);

    @media screen and (min-width: ${breakpoints.md}) {
        text-align: right;
    }
`;

const SecondaryRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        justify-content: flex-end;
    }
`;

const secondaryStyles = `
    padding: 0;
    border: none;
    background: none;
    color: ${colors.neutralLight};
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
`;

const SecondaryLink = styled.a`
    ${secondaryStyles}
`;

const SecondaryButton = styled.button`
    ${secondaryStyles}
`;

const Callout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 8px;
    padding: 18px 20px;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
    }
`;

const CalloutTitle = styled.p`
    margin: 0 0 4px 0;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CalloutText = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};
`;

const CalloutButton = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 18px;
    border-radius: 10px;
    border: none;
    background: ${panelAccent(colors.secondaryPurple)};
    color: ${colors.neutralLight};
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        opacity: 0.92;
    }
`;
